import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { setExpositorToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound, Loader2, ArrowRight, Plus, X, Layers, Lock, Info, AlertTriangle } from "lucide-react";
import { BRAND } from "@/lib/constants";

export default function Landing() {
  const [number, setNumber] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [requireCode, setRequireCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [comboMode, setComboMode] = useState(false);
  const [codes, setCodes] = useState([{ id: "r0", value: "" }, { id: "r1", value: "" }]);
  const [comboLoading, setComboLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [enteringIdx, setEnteringIdx] = useState(null);
  const navigate = useNavigate();

  const analyzeSpaces = async () => {
    const nums = codes.map((c) => c.value.trim().toUpperCase()).filter(Boolean);
    if (nums.length < 2) return toast.error("Ingresá al menos dos números de stand");
    if (requireCode && !accessCode.trim()) return toast.error("Ingresá tu código de acceso");
    setComboLoading(true);
    setAnalysis(null);
    try {
      const { data } = await api.post("/expositor/spaces/analyze", {
        stand_numbers: nums,
        access_code: accessCode.trim() || undefined,
      });
      if (data.expositor_token) setExpositorToken(data.expositor_token);
      setAnalysis(data);
      // Guardar en sessionStorage para "Mis espacios"
      sessionStorage.setItem("mis_espacios", JSON.stringify(data));
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "No se pudo analizar los espacios");
    } finally { setComboLoading(false); }
  };

  const enterGroup = async (group, idx) => {
    setEnteringIdx(idx);
    try {
      const { data } = await api.post("/expositor/spaces/enter", {
        stand_numbers: group.codes,
        access_code: accessCode.trim() || undefined,
      });
      if (data.expositor_token) setExpositorToken(data.expositor_token);
      toast.success(group.combined ? `Espacios unidos · ${group.codes.join(" + ")}` : `Ingresando a ${data.primary_stand}`);
      navigate(`/stand/${data.primary_stand}`);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "No se pudo ingresar al espacio");
    } finally { setEnteringIdx(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!number.trim()) return toast.error("Ingresá tu número de stand");
    if (requireCode && !accessCode.trim()) return toast.error("Ingresá tu código de acceso");
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await api.post("/expositor/access", {
        stand_number: number.trim(),
        access_code: accessCode.trim() || undefined,
      });
      if (data.expositor_token) setExpositorToken(data.expositor_token);
      toast.success(`Espacio encontrado · ${data.stand_number}`);
      navigate(`/stand/${data.stand_number}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Código inválido";
      toast.error(typeof msg === "string" ? msg : "Stand no encontrado");
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/expositor/access-mode").then(({ data }) => setRequireCode(!!data?.require_access_code)).catch(() => {});
  }, []);

  const fmtPrice = (n) => n == null ? "—" : `USD ${Number(n).toLocaleString("es-AR")}`;

  return (
    <div className="min-h-screen bg-[#09090B] bg-grain text-white flex flex-col">
      <header className="border-b border-zinc-800/60 backdrop-blur-xl bg-zinc-950/60">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-center sm:justify-start">
          <img src={BRAND.LOGO_AM} alt="Argentina Mining Norte 2026" className="h-11 w-auto" />
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12 md:py-16 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#FF5A00] mb-5">Edición Norte · 2026</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Configurá tu<br/>
            <span className="text-[#FF5A00]">Paquete Full Equipado.</span>
          </h1>
          <p className="mt-5 text-zinc-400 text-base md:text-lg max-w-xl leading-relaxed">
            Ingresá tu número de stand ya asignado para configurar el armado de tu espacio con el Paquete Full Equipado. Podés cargar datos, gráfica, terminaciones, contenido para TV y upgrades opcionales.
          </p>

          <form onSubmit={submit} className="mt-8 max-w-md space-y-3">
            <label className="text-xs uppercase tracking-wider text-zinc-500 block">Número de stand</label>
            <Input
              data-testid="stand-number-input"
              value={number}
              onChange={(e) => { setNumber(e.target.value.toUpperCase()); setNotFound(false); }}
              placeholder="Ej: R101 · F102 · 301 · T16"
              className="bg-zinc-950 border-zinc-800 h-12 text-base tracking-wider font-mono focus:border-[#FF5A00] focus:ring-[#FF5A00]/30"
              autoFocus
            />
            <label className="text-xs uppercase tracking-wider text-zinc-500 block pt-1">Código de acceso {requireCode ? "" : "(opcional durante la transición)"}</label>
            <div className="flex gap-2">
              <Input
                data-testid="access-code-input"
                value={accessCode}
                onChange={(e) => { setAccessCode(e.target.value.toUpperCase()); setNotFound(false); }}
                placeholder={requireCode ? "Ej: 7XK9-P4RM" : "Si Servicios Oficiales te lo envió"}
                className="bg-zinc-950 border-zinc-800 h-12 text-base tracking-wider font-mono focus:border-[#FF5A00] focus:ring-[#FF5A00]/30 flex-1"
              />
              <Button
                data-testid="submit-stand-btn"
                type="submit"
                disabled={loading}
                className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-12 px-6"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" />Ingresar <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              El código de acceso es entregado por Servicios Oficiales al contacto autorizado de cada expositor.
            </p>
            <p className="text-[11px] text-zinc-600 leading-relaxed">
              Tu espacio ya debe estar contratado previamente. Esta plataforma no reserva ni vende espacios; sólo permite configurar el Paquete Full Equipado asociado a tu stand.
            </p>
            {notFound && (
              <div data-testid="not-found-msg" className="p-3 border border-amber-500/40 bg-amber-500/5 rounded-sm text-xs text-amber-200">
                No encontramos ese número de stand. Revisá el dato ingresado o consultá a Rocky, nuestro asistente virtual.
              </div>
            )}

            <button
              type="button"
              data-testid="combo-toggle"
              onClick={() => { setComboMode(!comboMode); setAnalysis(null); }}
              className="flex items-center gap-2 text-xs text-zinc-400 hover:text-[#FF5A00] transition mt-2"
            >
              <Layers className="w-3.5 h-3.5" />
              {comboMode ? "Ocultar carga de múltiples espacios" : "Tengo dos o más espacios (contiguos o no)"}
            </button>

            {comboMode && (
              <div data-testid="combo-panel" className="border border-zinc-800 bg-zinc-950 rounded-sm p-4 space-y-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Cargá todos los stands que compraste</div>
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  Podés ingresar cualquier combinación de códigos, sean vecinos o no. El sistema detecta automáticamente cuáles comparten medianera y agrupa los espacios contiguos como un único stand; los no contiguos se configuran por separado.
                </p>
                {codes.map((c, i) => (
                  <div key={c.id} className="flex gap-2 items-center">
                    <Input
                      data-testid={`combo-code-${i}`}
                      value={c.value}
                      onChange={(e) => {
                        const next = codes.map((row, j) => j === i ? { ...row, value: e.target.value.toUpperCase() } : row);
                        setCodes(next);
                        setAnalysis(null);
                      }}
                      placeholder={i === 0 ? "Stand 1 · Ej: T16" : `Stand ${i + 1} · Ej: T17`}
                      className="bg-zinc-900 border-zinc-800 h-10 text-sm font-mono tracking-wider focus:border-[#FF5A00] flex-1"
                    />
                    {i >= 2 && (
                      <button type="button" data-testid={`combo-remove-${i}`} onClick={() => { setCodes(codes.filter((_, j) => j !== i)); setAnalysis(null); }} className="text-zinc-500 hover:text-red-400">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  <Button type="button" data-testid="combo-add-btn" onClick={() => setCodes([...codes, { id: `r${Date.now()}`, value: "" }])} variant="outline" className="border-zinc-700 hover:bg-zinc-900 rounded-sm h-9 text-xs">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Agregar otro espacio
                  </Button>
                  <Button type="button" data-testid="combo-verify-btn" onClick={analyzeSpaces} disabled={comboLoading} className="bg-zinc-100 text-zinc-900 hover:bg-white rounded-sm h-9 text-xs">
                    {comboLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Analizar espacios"}
                  </Button>
                </div>

                {analysis && (
                  <div data-testid="spaces-analysis" className="space-y-3 pt-2">
                    {analysis.has_multiple_groups && (
                      <div data-testid="multi-group-msg" className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 text-xs text-amber-200 flex gap-2">
                        <Info className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{analysis.info_message}</span>
                      </div>
                    )}
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">
                      {analysis.groups.length === 1 ? "Espacio detectado" : `Mis espacios · ${analysis.groups.length} configuraciones`}
                    </div>
                    {analysis.groups.map((g, i) => (
                      <div
                        key={g.codes.join("+")}
                        data-testid={`group-${i}`}
                        className={`border rounded-sm p-4 space-y-2 ${g.requires_manual_quote ? "border-amber-500/50 bg-amber-500/5" : "border-[#FF5A00]/40 bg-[#FF5A00]/5"}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                              Grupo {i + 1} · {g.combined ? "Contiguos (unidos)" : g.codes.length > 1 ? "Contiguos" : "Independiente"}
                            </div>
                            <div className="text-lg font-mono font-bold text-white mt-1">
                              {g.codes.join(" + ")}
                            </div>
                          </div>
                          {g.requires_manual_quote && (
                            <span className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-500/40 px-2 py-0.5 rounded-sm flex items-center gap-1 shrink-0">
                              <AlertTriangle className="w-3 h-3" /> A cotizar
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-zinc-300">
                          <div><span className="text-zinc-500">Superficie:</span> {g.superficie} m²</div>
                          <div><span className="text-zinc-500">Tipología:</span> {g.tipo_label}</div>
                          <div><span className="text-zinc-500">Paneles:</span> {g.n_paneles || "A definir"}</div>
                          <div>
                            <span className="text-zinc-500">Precio:</span>{" "}
                            {g.requires_manual_quote
                              ? <span className="text-amber-300 font-medium">A cotizar</span>
                              : <span className="text-[#FF5A00] font-mono font-medium">{fmtPrice(g.price)} + IVA</span>}
                          </div>
                        </div>
                        {!g.requires_manual_quote && g.price != null && g.extra_m2 > 0 && (
                          <div className="text-[10px] text-zinc-500">{g.formula}</div>
                        )}
                        {g.requires_manual_quote && (
                          <div className="text-[11px] text-amber-200/80 leading-relaxed">
                            Grupo Península / 1 pared. El precio queda pendiente de cotización manual por Servicios Oficiales. Podés continuar configurando paneles, gráficas, mobiliario y upgrades: administración recibirá un aviso automático.
                          </div>
                        )}
                        <Button
                          type="button"
                          data-testid={`group-enter-${i}`}
                          onClick={() => enterGroup(g, i)}
                          disabled={enteringIdx === i}
                          className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-10 w-full mt-2 text-xs"
                        >
                          {enteringIdx === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Ingresar a configurar <ArrowRight className="w-4 h-4 ml-2" /></>}
                        </Button>
                      </div>
                    ))}
                    {analysis.groups.length > 1 && (
                      <div className="text-[10px] text-zinc-500 pt-1 border-t border-zinc-800">
                        Cada grupo mantiene su propia configuración, paneles, gráficas, upgrades y estado. Los datos generales (empresa, contacto) pueden reutilizarse entre grupos.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        <div>
          <div className="relative rounded-md overflow-hidden border border-zinc-800">
            <img src={BRAND.STAND_PHOTO} alt="Paquete Full Equipado" className="w-full h-[420px] object-cover" />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-zinc-950/95 to-transparent">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-200 bg-zinc-950/70 backdrop-blur px-3 py-1.5 rounded-sm border border-white/10">
                <span className="w-1.5 h-1.5 bg-[#FF5A00] animate-pulse rounded-full" /> Paquete Full Equipado · Referencia visual
              </div>
            </div>
          </div>
        </div>
      </main>

      <section className="border-t border-zinc-900 py-14 bg-gradient-to-b from-transparent to-zinc-950">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <img src={BRAND.LOGO_SO} alt="Servicios Oficiales" className="h-24 md:h-28 w-auto mx-auto invert opacity-95" />
          <p className="mt-6 text-sm md:text-base text-zinc-300">
            Configuración y asistencia técnica a cargo de <span className="text-white font-medium">Servicios Oficiales</span>.
          </p>
          <p className="mt-1 text-xs text-zinc-500 uppercase tracking-[0.2em]">
            Exhibition Marketing and Logistics
          </p>
        </div>
      </section>

      <footer className="border-t border-zinc-900 py-6 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <span>Este configurador es para expositores que ya contrataron su espacio en Argentina Mining Norte 2026.</span>
          <a href="/admin" data-testid="admin-access-link" className="inline-flex items-center gap-1.5 text-zinc-600 hover:text-zinc-300 transition">
            <Lock className="w-3 h-3" /> Acceso administrador
          </a>
        </div>
      </footer>
    </div>
  );
}
